import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';

const navigationLinks = [
  { name: 'Services', path: '/#services', hasDropdown: true },
  { name: 'About', path: '/about' },
  { name: 'Team', path: '/team' },
  { name: 'NT Benefits', path: '/nt-benefits' },
  { name: 'Contact', path: '/#contact' },
];

const serviceLinks = [
  { name: 'Technical Advisory', path: '/services/technical-advisory' },
  { name: 'Sourcing', path: '/services/sourcing' },
  { name: 'Products', path: '/services/products' },
  { name: 'Renewable Energy', path: '/services/renewable-energy' },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);

    if (path.startsWith('/#')) {
      const sectionId = path.substring(2);
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(path);
    }
  };

  const isActive = (path) => {
    if (path.startsWith('/#')) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path.split('#')[0]);
  };

  return (
    <header className="sticky top-4 z-50 px-4 pt-0">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between h-14 px-6 rounded-full bg-white/60 backdrop-blur-xl border border-white/70 shadow-[0_8px_32px_-8px_rgba(80,50,20,0.12)]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" aria-label="GSD — Global Sales & Distribution">
            <img src="/logo.png" alt="GSD" className="h-9 w-auto object-contain" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#5a4a3a]">
            {navigationLinks.map((link) => (
              <div key={link.name} className="relative">
                {link.hasDropdown ? (
                  <div
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button
                      onClick={() => handleNavClick(link.path)}
                      className={`flex items-center gap-1 transition-colors ${location.pathname.includes('/services') ? 'text-[#2a2420] font-medium' : 'hover:text-[#2a2420]'}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isServicesOpen && (
                      <div className="absolute top-full left-0 pt-2 w-64">
                        <div className="bg-white/80 backdrop-blur-xl border border-white/80 rounded-2xl shadow-[0_16px_48px_-12px_rgba(80,50,20,0.2)] overflow-hidden p-2">
                          {serviceLinks.map((service) => (
                            <Link
                              key={service.name}
                              to={service.path}
                              className="block px-4 py-2.5 text-sm text-[#5a4a3a] hover:bg-[#c9a962]/10 hover:text-[#2a2420] rounded-xl transition-colors"
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => handleNavClick(link.path)}
                    className={`transition-colors ${isActive(link.path) ? 'text-[#2a2420] font-medium' : 'hover:text-[#2a2420]'}`}
                  >
                    {link.name}
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-2">
            <Link
              to="/pay"
              className="hidden md:inline-flex bg-[#2a2420] hover:bg-[#1a1410] text-[#fbf8f3] text-sm font-medium px-5 py-2 rounded-full transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              Pay Invoice
            </Link>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-[#2a2420] p-2 hover:text-[#8b5a2b] transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-2 bg-white/80 backdrop-blur-xl border border-white/80 rounded-2xl shadow-[0_16px_48px_-12px_rgba(80,50,20,0.2)] p-4">
            <nav className="flex flex-col">
              {navigationLinks.map((link) => (
                <div key={link.name}>
                  <button
                    onClick={() => {
                      if (link.hasDropdown) {
                        setIsServicesOpen(!isServicesOpen);
                      } else {
                        handleNavClick(link.path);
                      }
                    }}
                    className="flex items-center justify-between w-full py-2.5 text-sm text-[#2a2420]"
                  >
                    {link.name}
                    {link.hasDropdown && (
                      <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    )}
                  </button>

                  {link.hasDropdown && isServicesOpen && (
                    <div className="pl-3 mt-1 mb-2 border-l border-[#c9a962]/30">
                      {serviceLinks.map((service) => (
                        <Link
                          key={service.name}
                          to={service.path}
                          className="block py-2 text-sm text-[#5a4a3a] hover:text-[#2a2420]"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link
                to="/pay"
                className="mt-3 bg-[#2a2420] text-[#fbf8f3] text-sm font-medium py-3 rounded-full transition-colors text-center"
              >
                Pay Invoice
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
